<template>
  <div class="_taskView__task">
    <md-checkbox 
      v-model="isComplete"
      :title="title"
      :disabled="isBusy || isRefreshing || isTaskDisabled"
      @change="onToggle"
    />
    <span 
      title="View Task Details" 
      class="_taskView__task__label"
      @click="onViewTask"
    >{{ task.subject }}</span>

    <!-- ATLAS-1056: Final Note/Comment dialog -->
    <md-dialog :md-active.sync="isNoteDialogOpen" :md-click-outside-to-close="false" style="max-width: 840px;">
      <md-dialog-title>Add Comment</md-dialog-title>
      <md-dialog-content>
        <CaseTaskViewCommentComponent
          :recordData="commentTask"
          :atClose="onNoteDialogClose"
          :updateTaskComment="taskUpdateList"
        ></CaseTaskViewCommentComponent>
      </md-dialog-content>
    </md-dialog>

  </div>
</template>

<script>
import Amplify, { Auth, API, graphqlOperation } from 'aws-amplify';
import * as mutations from '@/graphql/mutations';

import CustomTaskForm from "@/components/DynamicForm/CustomTaskForm";
const CustomTaskFormMixin = {
  methods: {
    validateTaskForm: CustomTaskForm.methods.validateTaskForm,
  }
};

import CaseTaskViewCommentComponent from "@/components/DynamicForm/CaseTaskView/CaseTaskViewCommentComponent";

export default {
  mixins: [CustomTaskFormMixin],
  components: {
    CaseTaskViewCommentComponent
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
    isCompleteInOrder: {
      type: Boolean,
      required: false,
    },
    atTaskUpdate: {
      type: Function,
      required: true,
    },
    isRefreshing: {
      type: Boolean,
      required: false,
    },
    recordData: {}, // used for validating task using CustomTaskForm
    form: {}, // used for validating task using CustomTaskForm
  },
  data() {
    return {
      isBusy: false,
      isComplete: false,
      //the following are used for Commenting on checked task
      isNoteDialogOpen: false,
      commentTask: null,
      holdTaskUpdate: true,
      taskUpdateList: {
        skipTaskUpdate: true,
      }
    };
  },
  computed: {
    title() {
      if (this.isComplete || this.isTaskDisabled) {
        return '';
      }
      return 'Mark as Completed';
    },
    isTaskDisabled() {
      if ('Completed' == this.task.status || 'Closed_By_Workflow' === this.task.status) {
        return true;
      }
      if (this.isCompleteInOrder && !this.task.isAvailable) {
        return true;
      }
      const steps = this.$reduce(this, 'task.steps.items');
      if (!steps || !steps.length) {
        if (this.isCompleteInOrder) {
          return !this.task.isAvailable;
        }
        return false;
      }
      const completedSteps = steps.filter(_step => _step.status && 'complete' == _step.status.toLowerCase());
      return steps.length != completedSteps.length;
    },
  },
  methods: {
    async onToggle() {
      this.isBusy = true;
      window.$bus.$emit('sidepanel.tabs.disable');
      // Validate task submission using CustomTaskForm
      if (this.isComplete) {
        this.$set(this, 'recordData', this.task);
        this.$set(this, 'form', this.task);
        const isValid = await this.validateTaskForm();
        if (!isValid) {
          this.isComplete = false;
          this.isBusy = false;
          this.holdTaskUpdate = false
          window.$bus.$emit('sidepanel.tabs.enable');
          return;
        }
      }
      
      /**
       * ATLAS-1056: <Task> When closing out a task a dialog box should popup to add any final notes on the task
       */
      // if (this.holdTaskUpdate) {
      //   //open note/comment dialog
      //   this.isNoteDialogOpen = true;
      //   this.commentTask = this.task;
      //   return;
      // }
      // this.holdTaskUpdate = true;
      
      const operation = graphqlOperation(mutations.updateTask, this.getUpdatePayload());
      API.graphql(operation)
        .then(response => {
          console.log('Task Updte Success - ', response);
          this.atTaskUpdate();
          this.isBusy = false;
          window.$bus.$emit('sidepanel.tabs.enable');
          this.onAddTaskComment();
        })
        .catch(err => {
          this.isBusy = false;
          window.$bus.$emit('sidepanel.tabs.enable');
          console.log('Task Updte Error - ', err);
        });
    },
    getUpdatePayload() {
      const status = this.isComplete ? 'Completed' : 'Open';
      const payload = {
        input: {
          id: this.task.id,
          taskUpdatedById: this.$Session.userId,
          status,
        }
      };
      /**
       * ATLAS-840
       * @see https://zeamster.atlassian.net/browse/ATLAS-840
       */
      if (this.isComplete) {
        payload.input.dateCompleted = new Date().toISOString();
      }
      return payload;
    },
    /**
     * Using global event bus to emit an event when a user clicks on task label
     * @emits {Event} 
     */
    onViewTask() {
      this.$router.push({ path: "/cases" + "/" + this.task.case.id + "#/tasks/" + this.task.id});
      // window.$bus.$emit('caseTaskView.task.click', this.task);

      //ATLAS-1864: <Cases><View details> User is not scrolling to the add Comment box after marking task as completed
      const selector = '.vue-portal-target ._caseTask ._taskLayoutCard';
      const ele = document.querySelector(selector);
      const eleParent = ele.closest('._commentFeedSection');
      if (eleParent) {
        eleParent.scrollIntoView();
      }
    },
    onNoteDialogClose() {
      this.isNoteDialogOpen = false;
    },
    /**
     * @description
     * Open side panel and scroll to comments section
     * ATLAS-1233
     * <Case><Task><Comments> When click the check box, the user should be able
     * to see comments and sub comments made on the task.
     * 
     * @returns {Void}
     */
    onAddTaskComment() {
      // open side panel
      window.$bus.$emit('caseTaskView.task.click', this.task);
      
      // find comment box
      const selector = '.vue-portal-target ._caseTask ._taskLayoutCard';
      const ele = document.querySelector(selector);
      //ATLAS-1864: <Cases><View details> User is not scrolling to the add Comment box after marking task as completed
      const eleParent = ele.closest('._commentFeedSection');
      
      // if comment box is not already loaded then it will take around 5 seconds
      // else it takes around 2 seconds
      const time = eleParent ? 2000 : 5000;
      
      // function that scrolls to comment box
      const scrollToTaskFeed = () => {
        // const ele = document.querySelector(selector);
        if (eleParent) {
          eleParent.scrollIntoView();
        }
      }
      
      // set timeout
      setTimeout(scrollToTaskFeed, time);
    },
  },
  watch: {
    'task.status': {
      immediate: true,
      handler: function() {
        const status = this.$reduce(this, 'task.status');
        this.isComplete = 'Completed' == status ? true : false;
      }
    },
    /**
     * ATLAS-1056: <Task> When closing out a task a dialog box should popup to add any final notes on the task
     */
    'taskUpdateList.skipTaskUpdate': {
      immediate: true,
      handler: function() {
        if (!this.taskUpdateList.skipTaskUpdate || this.taskUpdateList.skipTaskUpdate === false) {
          this.holdTaskUpdate = false;
          //proceed to update task
          this.onToggle();
        }
      }
    },
  }
};
</script>

<style lang="scss" scoped>
</style>
